import { HTMLAttributes, useState } from "react";

export type DropPosition = "before" | "after";

/** Native HTML5 drag reorder: tracks the dragged row and which side of a row it would land. */
export function useDragReorder(onReorder: (from: number, to: number) => void) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [drop, setDrop] = useState<{ index: number; position: DropPosition } | null>(null);

  const end = () => {
    setDragIndex(null);
    setDrop(null);
  };

  const handlers = (index: number): HTMLAttributes<HTMLElement> => ({
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = "move";
      setDragIndex(index);
    },
    onDragOver: (e) => {
      if (dragIndex === null) return;
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const position = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
      if (drop?.index !== index || drop.position !== position) setDrop({ index, position });
    },
    onDrop: (e) => {
      e.preventDefault();
      if (dragIndex !== null && drop) {
        // Insertion point among the rows, then shift for the dragged row leaving its slot.
        let to = drop.position === "after" ? drop.index + 1 : drop.index;
        if (dragIndex < to) to -= 1;
        onReorder(dragIndex, to);
      }
      end();
    },
    onDragEnd: end,
  });

  const dropPositionFor = (index: number) =>
    drop?.index === index && dragIndex !== index ? drop.position : null;

  return { dragIndex, handlers, dropPositionFor };
}
